import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Plus, X } from "lucide-react";
import { sampleTemplateData } from "../data/mockData";
import { tagsAPI, referenceDataAPI } from "../services/api";
import EditTemplateConfirmationModal from "../Modal/EditTemplateConfirmationModal";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: min(760px, 95vw);
  height: min(760px, 90vh);
  max-width: 95vw;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 500;
  color: #111827;
  margin: 0;
  flex: 1;
  text-align: left;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  padding: 0.5rem;
  border-radius: 0.25rem;
  position: absolute;
  right: 0;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
`;

const ContentSection = styled.div`
  padding: 0;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.4rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8rem;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const TagContainer = styled.div`
  position: relative;
`;

const TagInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const TagInput = styled.input`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8rem;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const AddTagButton = styled.button`
  background-color: #f9fafb;
  border: 1px solid #d1d5db;
  color: #374151;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  background-color: transparent;
  border: 1px solid #00b5b8;
  color: #00b5b8;
  border-radius: 0px;
  padding: 0.25rem 0.5rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  height: 30px;
  width: auto;
  position: relative;
  cursor: pointer;
`;

const TagRemoveButton = styled.button`
  background: white;
  border: 1px solid #00b5b8;
  cursor: pointer;
  color: #00b5b8;
  padding: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  top: -8px;
  right: -8px;
`;

const GenderSection = styled.div`
  margin-bottom: 1rem;
`;

const GenderAgeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 0.5rem;
`;

const GenderColumn = styled.div`
  flex: 1;
`;

const AgeColumn = styled.div`
  flex: 1;
`;

const GenderOptions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.4rem;
`;

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const RadioInput = styled.input`
  width: 0.9rem;
  height: 0.9rem;
  accent-color: #1d87f0;

  &:checked {
    background-color: #1d87f0;
    border-color: #1d87f0;
  }
`;

const RadioLabel = styled.label`
  font-size: 0.8rem;
  color: #374151;
  cursor: pointer;
`;

const AgeRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.4rem;
`;

const AgeInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AgeInput = styled.input`
  width: 70px;
  padding: 0.4rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  text-align: center;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #1d87f0;
    box-shadow: 0 0 0 3px rgba(29, 135, 240, 0.1);
  }

  /* ซ่อน spinner buttons */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const AgeSeparator = styled.span`
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
`;

const MultiSelectContainer = styled.div`
  position: relative;
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
`;

const MultiSelectWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const MultiSelectButton = styled.button`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  background-color: white;
  cursor: pointer;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
`;

const SelectButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  height: fit-content;

  &:hover {
    background-color: #0891b2;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.8rem;

  &:focus {
    outline: none;
  }
`;

const ParameterOption = styled.div`
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const CategoryHeader = styled.div`
  padding: 0.35rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #00b5b8;
  background-color: #f0fafa;
  border-top: 1px solid #e5e7eb;
  pointer-events: none;
  user-select: none;

  &:first-child {
    border-top: none;
  }
`;

const Checkbox = styled.input`
  width: 0.8rem;
  height: 0.8rem;
  accent-color: #00b5b8;
`;

const TableContainer = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #f9fafb;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const TableHeaderCell = styled.th`
  padding: 0.5rem;
  text-align: left;
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
`;

const TableCell = styled.td`
  padding: 0.5rem;
  font-size: 0.8rem;
  color: #374151;
`;

const NormalRangeBadge = styled.span`
  background: transparent;
  color: #374151;
  padding: 0;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: normal;
`;

const RadioOptionsContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ParameterInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`;

const ParameterInput = styled.input`
  width: 65px;
  padding: 0.3rem 0.4rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  text-align: center;

  &::placeholder {
    color: #9ca3af;
    font-size: 0.7rem;
  }

  &:focus {
    outline: none;
    border-color: #1d87f0;
    box-shadow: 0 0 0 2px rgba(29, 135, 240, 0.1);
  }

  /* ซ่อน spinner buttons */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const ParameterSeparator = styled.span`
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 500;
`;

const SubTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem;
  margin: 0;
`;

const SubTableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const SubTableCell = styled.td`
  padding: 0.4rem 0.3rem;
  font-size: 0.7rem;
  color: #374151;
  text-align: center;
  vertical-align: top;
`;

const SubTableHeaderCell = styled.th`
  padding: 0.4rem 0.3rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  text-align: center;
  background-color: #f9fafb;
`;

const ActionSection = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const CancelButton = styled.button`
  background-color: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
  }
`;

const SaveButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background-color: #0891b2;
  }
`;

const EditTemplateModal = ({
  isOpen,
  onClose,
  onSave,
  templateData = null,
}) => {
  const [templateName, setTemplateName] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]); // tags ที่ดึงจาก API
  const [selectedGender, setSelectedGender] = useState("male");
  const [ageRange, setAgeRange] = useState(["", ""]); // เริ่มต้นเป็นค่าว่าง ให้ user กรอกเอง
  const [selectedParameters, setSelectedParameters] = useState([]);
  const [tempSelectedParameters, setTempSelectedParameters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [parameterRanges, setParameterRanges] = useState({});
  const [parameterRadioValues, setParameterRadioValues] = useState({}); // สำหรับเก็บค่า radio (ไม่มี/มี)
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [allParameters, setAllParameters] = useState([]);

  useEffect(() => {
    if (!isOpen) return;

    const loadTags = async () => {
      try {
        const response = await tagsAPI.getUsedTags();
        if (response.status === "success" && response.data) {
          setAvailableTags(response.data);
        }
      } catch (error) {
        console.error("✗ Error loading tags:", error);
        setAvailableTags([]);
      }
    };

    const loadParameters = async () => {
      try {
        const response = await referenceDataAPI.getParametersList();
        if (response.status === "success" && response.data) {
          const formattedParams = response.data.map((param) => ({
            name: param.name,
            unit: param.unit || "",
            normalRange: param.normalRange || [0, 1000],
            normalValue: param.normalValue || null,
            defaultRange: param.normalRange || [0, 100],
            category: param.category,
            allNormalRanges: param.allNormalRanges || [],
          }));
          setAllParameters(formattedParams);
        }
      } catch (error) {
        console.error("✗ Error loading parameters:", error);
        setAllParameters([]);
      }
    };

    loadTags();
    loadParameters();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && templateData) {
      const dataToLoad = templateData || sampleTemplateData;
      setTemplateName(dataToLoad.templateName);
      setCreatedBy(dataToLoad.createdBy);
      setTags([...dataToLoad.tags]);
      setSelectedGender(dataToLoad.gender);

      const loadedAgeRange = Array.isArray(dataToLoad.ageRange)
        ? [dataToLoad.ageRange[0] ?? "", dataToLoad.ageRange[1] ?? ""]
        : ["", ""];
      setAgeRange(loadedAgeRange);

      setSelectedParameters(dataToLoad.selectedParameters);
      setParameterRanges({ ...dataToLoad.parameterRanges });
      if (dataToLoad.parameterRadioValues) {
        setParameterRadioValues({ ...dataToLoad.parameterRadioValues });
      }
    }
  }, [isOpen, templateData]);

  // Merge normalValue จาก allParameters หลังโหลดเสร็จ เพื่อให้ได้ค่าปกติที่ถูกต้องจาก medium.json
  useEffect(() => {
    if (selectedParameters.length > 0 && allParameters.length > 0) {
      const needsMerge = selectedParameters.some(
        (p) => !p.normalValue || p.normalValue === null
      );

      if (needsMerge) {
        const mergedParameters = selectedParameters.map((param) => {
          const fullParam = allParameters.find((p) => p.name === param.name);

          return {
            ...param,
            normalValue: fullParam !== undefined ? (fullParam.normalValue || null) : (param.normalValue || null),
            normalRange: fullParam?.normalRange ||
              param.normalRange || [0, 100],
            unit: fullParam?.unit || param.unit || "",
            allNormalRanges:
              fullParam?.allNormalRanges || param.allNormalRanges || [],
          };
        });

        setSelectedParameters(mergedParameters);
      }
    }
  }, [allParameters, selectedParameters.length]);

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleMinAgeChange = (e) => {
    let value = e.target.value;
    if (value === "" || value === ".") {
      setAgeRange([value, ageRange[1]]);
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      const roundedValue = Math.round(numValue * 1000) / 1000;
      setAgeRange([roundedValue, ageRange[1]]);
    }
  };

  const handleMaxAgeChange = (e) => {
    let value = e.target.value;
    if (value === "" || value === ".") {
      setAgeRange([ageRange[0], value]);
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      const roundedValue = Math.round(numValue * 1000) / 1000;
      setAgeRange([ageRange[0], roundedValue]);
    }
  };

  const cleanParameterName = (name) => {
    if (name && name.startsWith("_hide_")) {
      return name.substring(6);
    }
    return name;
  };

  const filteredParameters = allParameters.filter((param) =>
    param.normalValue !== null && param.normalValue !== '' &&
    param.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleParameterToggle = (parameter) => {
    const isSelected = tempSelectedParameters.some(
      (p) => p.name === parameter.name
    );
    if (isSelected) {
      setTempSelectedParameters(
        tempSelectedParameters.filter((p) => p.name !== parameter.name)
      );
    } else {
      setTempSelectedParameters([...tempSelectedParameters, parameter]);
    }
  };

  const isRadioParameter = (parameter) => {
    const radioParams = [
      "Color",
      "Clarity",
      "Color (Stool)",
      "Appearance (Stool)",
      "Parasite",
      "RBC Index",
    ];
    return radioParams.includes(parameter.name);
  };

  const isDropdownParameter = (parameter) => {
    const dropdownParams = [
      "Protein", "Sugar", "Glucose", "Ketones", "Blood",
      "Leukocytes", "Nitrite", "Bilirubin", "Urobilinogen", "Bacteria",
    ];
    return dropdownParams.includes(parameter.name);
  };

  const isCountDropdownParameter = (parameter) => {
    const countParams = [
      "RBC (Urine)", "Red Blood Cells",
      "WBC (Urine)", "White Blood Cells",
      "Epithelial cell", "เซลล์เยื่อบุผิว",
    ];
    return countParams.includes(parameter.name);
  };

  const isNegPosParameter = (parameter) => {
    const negPosParams = ["HBsAb", "HBsAg", "HCV Ab", "Hemoglobin detection stool"];
    return negPosParams.includes(parameter.name);
  };

  const DROPDOWN_OPTIONS = ["Negative", "Trace", "1+", "2+", "3+", "4+"];
  const NEG_POS_OPTIONS = ["Negative", "Positive"];
  const COUNT_DROPDOWN_OPTIONS = ["0-1", "1-2", "2-3", "3-5", "5-10", "10-20", "20-30", "30-50", "50-100", "100-200", ">200"];

  const getRadioOptions = (parameterName) => {
    const optionsMap = {
      Color: ["เหลืองอ่อน", "ผิดปกติ"],
      Clarity: ["Clear", "ผิดปกติ"],
      "Color (Stool)": ["xxx"],
      "Appearance (Stool)": ["xxx"],
      "WBC (Stool)": ["Not found", "Found"],
      "RBC (Stool)": ["Not found", "Found"],
      Parasite: ["Not found", "Found"],
      "RBC Index": ["xxx"],
      eGFR: ["xxx"],
    };
    return optionsMap[parameterName] || ["ไม่มี", "มี"];
  };

  const handleSelectParameters = () => {
    setSelectedParameters([...tempSelectedParameters]);

    const newRanges = { ...parameterRanges };
    const newRadioValues = { ...parameterRadioValues };

    Object.keys(newRanges).forEach((paramName) => {
      if (!tempSelectedParameters.some((p) => p.name === paramName)) {
        delete newRanges[paramName];
      }
    });

    Object.keys(newRadioValues).forEach((paramName) => {
      if (!tempSelectedParameters.some((p) => p.name === paramName)) {
        delete newRadioValues[paramName];
      }
    });

    tempSelectedParameters.forEach((parameter) => {
      if (isNegPosParameter(parameter)) {
        if (!newRadioValues[parameter.name]) {
          newRadioValues[parameter.name] = "Negative";
        }
      } else if (isCountDropdownParameter(parameter) || parameter.name === "WBC (Stool)" || parameter.name === "RBC (Stool)") {
        if (!newRanges[parameter.name]) {
          newRanges[parameter.name] = ["0-1", "0-1"];
        }
      } else if (isRadioParameter(parameter)) {
        if (!newRadioValues[parameter.name]) {
          const options = getRadioOptions(parameter.name);
          newRadioValues[parameter.name] = options[0];
        }
      } else if (isDropdownParameter(parameter)) {
        if (!newRanges[parameter.name]) {
          newRanges[parameter.name] = ["Negative", "Negative"];
        }
      } else {
        if (!newRanges[parameter.name]) {
          newRanges[parameter.name] = ["", ""];
        }
      }
    });

    setParameterRanges(newRanges);
    setParameterRadioValues(newRadioValues);
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  const handleDropdownOpen = () => {
    setTempSelectedParameters([...selectedParameters]);
    setIsDropdownOpen(true);
  };

  const getPlaceholderFromNormalValue = (normalValue) => {
    if (!normalValue) return ["", ""];

    const trimmed = normalValue.trim();

    if (trimmed.startsWith(">")) {
      const val = trimmed.substring(1);
      return ["0", val];
    } else if (trimmed.startsWith("<")) {
      const val = trimmed.substring(1);
      return ["0", val];
    } else if (trimmed.includes("-")) {
      const parts = trimmed.split("-");
      return parts.length === 2 ? [parts[0], parts[1]] : ["", ""];
    } else {
      return [trimmed, trimmed];
    }
  };

  const handleParameterRangeChange = (paramName, isMin, value) => {
    const currentRange = parameterRanges[paramName] || [0, 100];

    if (value === "" || value === ".") {
      const newRange = isMin
        ? [value, currentRange[1]]
        : [currentRange[0], value];
      setParameterRanges({
        ...parameterRanges,
        [paramName]: newRange,
      });
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      const newRange = isMin
        ? [numValue, currentRange[1]]
        : [currentRange[0], numValue];
      setParameterRanges({
        ...parameterRanges,
        [paramName]: newRange,
      });
    }
  };

  const handleRadioChange = (paramName, value) => {
    setParameterRadioValues({
      ...parameterRadioValues,
      [paramName]: value,
    });
  };

  const handleDropdownChange = (paramName, isMin, value) => {
    const currentRange = parameterRanges[paramName] || ["Negative", "Negative"];
    const newRange = isMin ? [value, currentRange[1]] : [currentRange[0], value];
    setParameterRanges({ ...parameterRanges, [paramName]: newRange });
  };

  const handleSave = () => {
    setShowConfirmation(true);
  };

  const handleConfirmSave = () => {
    const updatedTemplate = {
      templateName,
      createdBy,
      tags,
      gender: selectedGender,
      ageRange,
      selectedParameters,
      parameterRanges,
      parameterRadioValues,
    };
    onSave(updatedTemplate);
    setShowConfirmation(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>แก้ไข Template</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ContentSection>
            <FormGroup>
              <Label>ชื่อ Template</Label>
              <Input
                type="text"
                placeholder="ชื่อ Template"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>ชื่อผู้สร้าง</Label>
              <Input
                type="text"
                placeholder="ชื่อผู้สร้าง"
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>ป้ายกำกับ (Tag)</Label>
              <TagContainer>
                <TagInputContainer>
                  <TagInput
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        addTag();
                      }
                    }}
                  />
                  <AddTagButton onClick={addTag}>เพิ่ม</AddTagButton>
                </TagInputContainer>

                <TagList>
                  {tags.map((tag, index) => (
                    <Tag key={index}>
                      <span>{tag}</span>
                      <TagRemoveButton onClick={() => removeTag(tag)}>
                        <X size={8} />
                      </TagRemoveButton>
                    </Tag>
                  ))}
                </TagList>
              </TagContainer>
            </FormGroup>

            <GenderSection>
              <GenderAgeRow>
                <GenderColumn>
                  <Label>สำหรับเพศ</Label>
                  <GenderOptions>
                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="male"
                        name="gender"
                        value="male"
                        checked={selectedGender === "male"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="male">ชาย</RadioLabel>
                    </RadioContainer>

                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="female"
                        name="gender"
                        value="female"
                        checked={selectedGender === "female"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="female">หญิง</RadioLabel>
                    </RadioContainer>

                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="both"
                        name="gender"
                        value="both"
                        checked={selectedGender === "both"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="both">ทุกเพศ</RadioLabel>
                    </RadioContainer>
                  </GenderOptions>
                </GenderColumn>

                <AgeColumn>
                  <Label>ช่วงอายุ (ปี)</Label>
                  <AgeRangeContainer>
                    <AgeInputWrapper>
                      <AgeInput
                        type="number"
                        step="0.001"
                        min="0"
                        placeholder="0.008"
                        value={ageRange[0]}
                        onChange={handleMinAgeChange}
                      />
                      <AgeSeparator>-</AgeSeparator>
                      <AgeInput
                        type="number"
                        step="0.001"
                        min="0"
                        placeholder="40"
                        value={ageRange[1]}
                        onChange={handleMaxAgeChange}
                      />
                    </AgeInputWrapper>
                  </AgeRangeContainer>
                </AgeColumn>
              </GenderAgeRow>
            </GenderSection>

            <FormGroup>
              <Label>เลือกพารามิเตอร์</Label>
              <MultiSelectContainer>
                <MultiSelectWrapper>
                  <MultiSelectButton type="button" onClick={handleDropdownOpen}>
                    <span>
                      {selectedParameters.length === 0
                        ? "เลือกพารามิเตอร์"
                        : `${selectedParameters.length} selected`}
                    </span>
                    <span>{isDropdownOpen ? "▲" : "▼"}</span>
                  </MultiSelectButton>

                  {isDropdownOpen && (
                    <DropdownContent>
                      <SearchInput
                        type="text"
                        placeholder="ค้นหาพารามิเตอร์..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {(() => {
                        const groups = [];
                        const seen = new Map();
                        filteredParameters.forEach((param) => {
                          const cat = param.category || 'อื่นๆ';
                          if (!seen.has(cat)) {
                            seen.set(cat, []);
                            groups.push({ category: cat, params: seen.get(cat) });
                          }
                          seen.get(cat).push(param);
                        });
                        return groups.map(({ category, params }) => (
                          <React.Fragment key={category}>
                            <CategoryHeader>{category}</CategoryHeader>
                            {params.map((parameter, index) => (
                              <ParameterOption
                                key={index}
                                onClick={() => handleParameterToggle(parameter)}
                              >
                                <Checkbox
                                  type="checkbox"
                                  checked={tempSelectedParameters.some(
                                    (p) => p.name === parameter.name
                                  )}
                                  onChange={() => {}}
                                />
                                <span>
                                  {cleanParameterName(parameter.name)} ({parameter.unit})
                                </span>
                              </ParameterOption>
                            ))}
                          </React.Fragment>
                        ));
                      })()}
                    </DropdownContent>
                  )}
                </MultiSelectWrapper>

                <SelectButton onClick={handleSelectParameters}>
                  เลือก
                </SelectButton>
              </MultiSelectContainer>
            </FormGroup>

            <TableContainer>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>พารามิเตอร์</TableHeaderCell>
                    <TableHeaderCell>ค่าปกติ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่กำหนด</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <tbody>
                  {selectedParameters.length > 0 ? (
                    selectedParameters.map((param, index) => {
                      const isNegPos = isNegPosParameter(param);
                      const isRadio = isRadioParameter(param);

                      if (isNegPos) {
                        const negPosValue = parameterRadioValues[param.name] || "Negative";
                        const allNormalRanges = param.allNormalRanges || [];

                        return (
                          <TableRow key={index}>
                            <TableCell>{param.name}</TableCell>
                            <TableCell>
                              {allNormalRanges.length > 0 ? (
                                <div style={{ padding: "0.5rem" }}>
                                  <SubTable>
                                    <thead>
                                      <tr>
                                        <SubTableHeaderCell>เพศ</SubTableHeaderCell>
                                        <SubTableHeaderCell>อายุ</SubTableHeaderCell>
                                        <SubTableHeaderCell>ค่าปกติ</SubTableHeaderCell>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {allNormalRanges.map((range, idx) => (
                                        <SubTableRow key={idx}>
                                          <SubTableCell>{range.sex}</SubTableCell>
                                          <SubTableCell>{range.age_range}</SubTableCell>
                                          <SubTableCell>{range.normal_range}</SubTableCell>
                                        </SubTableRow>
                                      ))}
                                    </tbody>
                                  </SubTable>
                                </div>
                              ) : (
                                <NormalRangeBadge>{param.normalValue ?? "null"}</NormalRangeBadge>
                              )}
                            </TableCell>
                            <TableCell>
                              <select
                                value={negPosValue}
                                onChange={(e) => handleRadioChange(param.name, e.target.value)}
                                style={{ padding: "0.25rem 0.5rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.8rem", cursor: "pointer" }}
                              >
                                {NEG_POS_OPTIONS.map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            </TableCell>
                          </TableRow>
                        );
                      }

                      if (isRadio) {
                        const options = getRadioOptions(param.name);
                        const radioValue =
                          parameterRadioValues[param.name] || options[0];

                        return (
                          <TableRow key={index}>
                            <TableCell>{param.name}</TableCell>
                            <TableCell>
                              <NormalRangeBadge>
                                {param.normalValue ?? "null"}
                              </NormalRangeBadge>
                            </TableCell>
                            <TableCell>
                              <RadioOptionsContainer>
                                {options.map((option, optionIndex) => (
                                  <RadioContainer key={optionIndex}>
                                    <RadioInput
                                      type="radio"
                                      id={`${param.name}-${optionIndex}`}
                                      name={param.name}
                                      value={option}
                                      checked={radioValue === option}
                                      onChange={(e) =>
                                        handleRadioChange(
                                          param.name,
                                          e.target.value
                                        )
                                      }
                                    />
                                    <RadioLabel
                                      htmlFor={`${param.name}-${optionIndex}`}
                                    >
                                      {option}
                                    </RadioLabel>
                                  </RadioContainer>
                                ))}
                              </RadioOptionsContainer>
                            </TableCell>
                          </TableRow>
                        );
                      } else {
                        const range = parameterRanges[param.name] || ["", ""];
                        const [minPlaceholder, maxPlaceholder] =
                          getPlaceholderFromNormalValue(param.normalValue);
                        const allNormalRanges = param.allNormalRanges || [];
                        const isDropdown = isDropdownParameter(param);
                        const isCountDrop = isCountDropdownParameter(param) || param.name === "WBC (Stool)" || param.name === "RBC (Stool)";

                        return (
                          <TableRow key={index}>
                            <TableCell>{param.name}</TableCell>
                            <TableCell
                              style={{
                                padding:
                                  allNormalRanges.length > 0 ? 0 : undefined,
                              }}
                            >
                              {allNormalRanges.length > 0 ? (
                                <div style={{ padding: "0.5rem" }}>
                                  <SubTable>
                                    <thead>
                                      <tr>
                                        <SubTableHeaderCell>
                                          เพศ
                                        </SubTableHeaderCell>
                                        <SubTableHeaderCell>
                                          อายุ
                                        </SubTableHeaderCell>
                                        <SubTableHeaderCell>
                                          ค่าปกติ
                                        </SubTableHeaderCell>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {allNormalRanges.map((range, idx) => (
                                        <SubTableRow key={idx}>
                                          <SubTableCell>
                                            {range.sex}
                                          </SubTableCell>
                                          <SubTableCell>
                                            {range.age_range}
                                          </SubTableCell>
                                          <SubTableCell>
                                            {range.normal_range}
                                          </SubTableCell>
                                        </SubTableRow>
                                      ))}
                                    </tbody>
                                  </SubTable>
                                </div>
                              ) : (
                                <NormalRangeBadge>
                                  {param.normalValue ?? "null"}
                                </NormalRangeBadge>
                              )}
                            </TableCell>
                            <TableCell>
                              {isDropdown ? (
                                <ParameterInputWrapper>
                                  <select
                                    value={range[0] || "Negative"}
                                    onChange={(e) => handleDropdownChange(param.name, true, e.target.value)}
                                    style={{ width: "65px", padding: "0.25rem 0.2rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.75rem", cursor: "pointer" }}
                                  >
                                    {DROPDOWN_OPTIONS.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                                  </select>
                                  <ParameterSeparator>-</ParameterSeparator>
                                  <select
                                    value={range[1] || "Negative"}
                                    onChange={(e) => handleDropdownChange(param.name, false, e.target.value)}
                                    style={{ width: "65px", padding: "0.25rem 0.2rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.75rem", cursor: "pointer" }}
                                  >
                                    {DROPDOWN_OPTIONS.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                                  </select>
                                </ParameterInputWrapper>
                              ) : isCountDrop ? (
                                <ParameterInputWrapper>
                                  <select
                                    value={range[0] || "0-1"}
                                    onChange={(e) => handleDropdownChange(param.name, true, e.target.value)}
                                    style={{ width: "75px", padding: "0.25rem 0.2rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.75rem", cursor: "pointer" }}
                                  >
                                    {COUNT_DROPDOWN_OPTIONS.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                                  </select>
                                  <ParameterSeparator>-</ParameterSeparator>
                                  <select
                                    value={range[1] || "0-1"}
                                    onChange={(e) => handleDropdownChange(param.name, false, e.target.value)}
                                    style={{ width: "75px", padding: "0.25rem 0.2rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.75rem", cursor: "pointer" }}
                                  >
                                    {COUNT_DROPDOWN_OPTIONS.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                                  </select>
                                </ParameterInputWrapper>
                              ) : (
                                <ParameterInputWrapper>
                                  <ParameterInput
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder={minPlaceholder}
                                    value={range[0]}
                                    onChange={(e) =>
                                      handleParameterRangeChange(
                                        param.name,
                                        true,
                                        e.target.value
                                      )
                                    }
                                  />
                                  <ParameterSeparator>-</ParameterSeparator>
                                  <ParameterInput
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder={maxPlaceholder}
                                    value={range[1]}
                                    onChange={(e) =>
                                      handleParameterRangeChange(
                                        param.name,
                                        false,
                                        e.target.value
                                      )
                                    }
                                  />
                                </ParameterInputWrapper>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      }
                    })
                  ) : (
                    <TableRow>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            </TableContainer>
          </ContentSection>

          <ActionSection>
            <CancelButton onClick={onClose}>ยกเลิก</CancelButton>
            <SaveButton onClick={handleSave}>ยืนยัน</SaveButton>
          </ActionSection>
        </ModalBody>
      </ModalContainer>

      <EditTemplateConfirmationModal
        isOpen={showConfirmation}
        onConfirm={handleConfirmSave}
      />
    </ModalOverlay>
  );
};

export default EditTemplateModal;
